import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiPublic } from "@/lib/api";
import { ArrowUpRight, ArrowLeft, User as UserIcon } from "lucide-react";

export default function Governance() {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        apiPublic.get("/governance")
            .then(r => setMembers(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="min-h-screen bg-[#050505] text-white" data-testid="governance-page">
            {/* NAV */}
            <nav className="fixed top-0 inset-x-0 z-50 border-b border-zinc-900/70 bg-[#050505]/80 backdrop-blur-md">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3" data-testid="gov-page-nav-logo">
                        <div className="h-7 w-7 rounded-full bg-tropical grain" />
                        <span className="font-display text-xl tracking-tight">StartupItalia<span className="text-gradient-tropical">.</span></span>
                    </Link>
                    <div className="hidden md:flex items-center gap-8 font-grotesk text-xs uppercase tracking-widest text-zinc-400">
                        <Link to="/#numeri" className="hover:text-white transition" data-testid="gov-page-nav-numeri">I numeri</Link>
                        <Link to="/governance" className="text-white transition" data-testid="gov-page-nav-governance">Governance</Link>
                        <Link to="/#investor" className="hover:text-white transition" data-testid="gov-page-nav-investor">Investor Relations</Link>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link to="/login" className="btn-rebel" data-testid="gov-page-nav-login">
                            Accedi <ArrowUpRight className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
            </nav>

            {/* HERO */}
            <section className="relative pt-40 pb-16 lg:pt-48 lg:pb-24 overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(101,63,205,0.18),transparent_55%),radial-gradient(circle_at_75%_70%,rgba(223,70,231,0.14),transparent_60%)] pointer-events-none" />
                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
                    <Link to="/" className="inline-flex items-center gap-2 text-zinc-500 hover:text-white transition text-xs uppercase tracking-widest font-grotesk mb-10" data-testid="gov-back-home">
                        <ArrowLeft className="h-3 w-3" /> Torna alla home
                    </Link>
                    <div>
                        <span className="label-tag fade-up" data-testid="gov-page-label">— Governance</span>
                    </div>
                    <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl mt-4 tracking-tighter fade-up delay-1" data-testid="gov-page-title">
                        Le persone <span className="font-serif-italic text-gradient-tropical">dietro</span><br />la visione.
                    </h1>
                    <p className="mt-8 text-zinc-400 leading-relaxed max-w-2xl text-lg fade-up delay-2" data-testid="gov-page-intro">
                        Il Consiglio di Amministrazione di StartupItalia rappresenta un mix unico di esperienza editoriale, finanziaria e di innovazione tecnologica. Clicca su un membro per scoprirne il profilo completo.
                    </p>
                </div>
            </section>

            {/* GRID */}
            <section className="pb-32">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                    {loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gov-page-loading">
                            {[0, 1, 2, 3, 4, 5].map(i => (
                                <div key={i} className="bento p-6 animate-pulse" data-testid={`gov-page-skeleton-${i}`}>
                                    <div className="aspect-[4/5] bg-white/5 mb-5 rounded-2xl" />
                                    <div className="h-4 w-3/4 bg-white/10 rounded mb-2" />
                                    <div className="h-3 w-1/2 bg-white/5 rounded" />
                                </div>
                            ))}
                        </div>
                    ) : members.length === 0 ? (
                        <div className="bento p-12 text-center text-zinc-500" data-testid="gov-page-empty">
                            Nessun membro pubblicato. Torna presto.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gov-page-grid">
                            {members.map((p, i) => (
                                <Link
                                    key={p.id}
                                    to={`/governance/${p.slug}`}
                                    className="bento p-0 overflow-hidden group transition-transform duration-500 hover:-translate-y-1"
                                    data-testid={`gov-page-member-${i}`}
                                >
                                    <div className="aspect-[4/5] bg-tropical-soft relative grain overflow-hidden">
                                        {p.photo_url ? (
                                            <img
                                                src={p.photo_url}
                                                alt={p.nome}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <UserIcon className="h-24 w-24 text-white/40" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-6">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <h3 className="font-display text-2xl tracking-tight">{p.nome}</h3>
                                                <p className="text-zinc-500 text-[11px] uppercase tracking-widest font-grotesk mt-2">{p.titolo}</p>
                                            </div>
                                            <ArrowUpRight className="h-5 w-5 text-zinc-600 group-hover:text-white group-hover:-translate-y-1 group-hover:translate-x-1 transition" />
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* FOOTER MINI */}
            <footer className="border-t border-zinc-900 py-10">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-xs uppercase tracking-widest font-grotesk text-zinc-600">
                    Powered by StartupItalia · v1.0
                </div>
            </footer>
        </div>
    );
}
